import type React from "react";
import { useEffect, useState } from "react";

import type { AdminTicketCommentDto, UserDto } from "@/api/types";
import type { CommentFieldProps } from "@/components/CommentField/CommentField";
import { CommentField } from "@/components/CommentField/CommentField";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { useDocumentInput } from "@/components/DocumentInput/useDocumentInput";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { useImageInput } from "@/components/ImageInput/useImageInput";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useContentFieldManager } from "@/providers/ContentFieldProvider";
import { useTranslation } from "@/translations";

import type { TicketCommentPayload, TicketCommentType } from "./helpers";

type TicketCommentFieldProps = {
  user: UserDto;
  commentType: TicketCommentType;
  commentInEditing?: AdminTicketCommentDto;
  actionComponent?: React.ReactNode;
  isAttachmentAllowed: boolean;
  placeholder?: string;
  onSend: ({ content, type, images, documents }: TicketCommentPayload) => Promise<void>;
  onCancelEdit?: () => void;
};

export function TicketCommentField({
  user,
  commentType,
  commentInEditing,
  actionComponent,
  isAttachmentAllowed,
  placeholder,
  onSend,
  onCancelEdit,
}: TicketCommentFieldProps): React.ReactNode {
  // Attachments
  const [images, setImages] = useState<FormImage[]>([]);
  const [videos, setVideos] = useState<FormVideo[]>([]);
  const [documents, setDocuments] = useState<FormDocument[]>([]);
  // Content
  const { ref, currValue, setCurrValue, onReset } = useContentFieldManager();
  const { addImages, removeImage, removeImages } = useImageInput({
    selectedImages: images,
    onChange: setImages,
  });
  const { addDocuments, removeDocument, removeDocuments } = useDocumentInput({
    selectedDocuments: documents,
    maximumFiles: 1,
    onChange: setDocuments,
  });
  const { t } = useTranslation();

  const onVideoUploadProgress = ({ name, progress }: { name: string; progress: number }) => {
    setVideos(
      [...videos].map((video) => {
        if (!isVideoUploaded(video) && video.file.name === name) {
          return {
            ...video,
            uploadProgress: progress,
          };
        }

        return video;
      }),
    );
  };
  const { uploadFormVideo } = useUploadVideo({
    onProgress: onVideoUploadProgress,
  });
  const { addVideos, removeVideo, removeVideos } = useVideoInput({
    selectedVideos: videos,
    onChange: setVideos,
    uploadFn: uploadFormVideo,
  });

  useEffect(() => {
    if (commentInEditing) {
      setCurrValue(commentInEditing.content ?? "");
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setImages(commentInEditing.images ?? []);
      setVideos(commentInEditing.videos ?? []);
      setDocuments(commentInEditing.documents ?? []);
    }
  }, [commentInEditing, setDocuments, setImages, setVideos, setCurrValue]);

  const onResetField = () => {
    removeImages();
    removeVideos();
    removeDocuments();
    onReset();

    if (commentInEditing) {
      onCancelEdit?.();
    }
  };

  const onSubmit = async () => {
    await onSend({
      content: currValue,
      type: commentType,
      images,
      videos,
      documents,
    });

    onResetField();
  };

  const getAllowedAttachments = () => {
    if (!isAttachmentAllowed) {
      return [];
    }
    const allowedAttachments: CommentFieldProps["allowedAttachments"] = ["image", "video"];
    if (user.isAdmin) {
      allowedAttachments.push("document");
    }

    return allowedAttachments;
  };

  return (
    <CommentField
      ref={ref}
      allowedAttachments={getAllowedAttachments()}
      tooltips={{
        send:
          commentType === "internal"
            ? t("page.tickets.details.comment-field.send-note-tooltip")
            : t("page.tickets.details.comment-field.send-message-tooltip"),
      }}
      onAddImages={addImages}
      onRemoveImage={removeImage}
      onAddVideo={addVideos}
      onRemoveVideo={removeVideo}
      onAddDocument={addDocuments}
      onRemoveDocument={removeDocument}
      placeholder={placeholder}
      onChange={(value) => {
        setCurrValue(value);
      }}
      isEdit={!!commentInEditing}
      onCancel={commentInEditing ? onResetField : undefined}
      autoFocus
      actionComponent={commentType === "public" ? actionComponent : undefined}
      value={currValue}
      disableSubmitOnEnter
      {...{
        user,
        images,
        videos,
        documents,
        onSubmit,
      }}
    />
  );
}
